import {
  NetAmountFilter,
  TransferType,
  useAddConsumptionToTransactionMutation,
  useTransactionsLazyQuery,
} from '@transactions/graphql';
import Money from 'common/Money';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from 'lucide-react';
import * as React from 'react';
import {
  ErrorText,
  InfoText,
  TransactionList,
  TransactionRow,
} from 'src/AccountLedgerView/TransactionSelectorComponents';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import {CategoryFilterInput} from 'src/CategoryFilterInput';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import {DimensionsFilterInput} from 'src/DimensionsFilterInput';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import {SearchInput} from 'src/SearchInput';
import {TransferTypeSelect} from 'src/TransferKindSelect';
import type {
  BasicRawTransactionFragment,
  TransactionsQueryVariables,
  UnifiedTransactionFragment,

  FieldFilterInput as FieldFilterInputType} from '@transactions/graphql';
import type {DateFilterState} from 'src/components/DateFilterSelect';
import type {SystemProps} from 'src/Modals';

const PAGE_SIZE = 20;

const netAmountFilterLabels: Record<NetAmountFilter, string> = {
  [NetAmountFilter.All]: 'All Transactions',
  [NetAmountFilter.Spending]: 'Spending',
  [NetAmountFilter.Income]: 'Income',
  [NetAmountFilter.Balanced]: 'Transfers',
};

const isNetAmountFilter = (value: string): value is NetAmountFilter =>
  value === String(NetAmountFilter.All) ||
  value === String(NetAmountFilter.Spending) ||
  value === String(NetAmountFilter.Income) ||
  value === String(NetAmountFilter.Balanced);

const parseNetAmountFilter = (value: string): NetAmountFilter =>
  isNetAmountFilter(value) ? value : NetAmountFilter.All;

const transactionLabel = (tx: UnifiedTransactionFragment): string =>
  tx.payee ?? tx.transferType ?? tx.description ?? 'Untitled';

type TransitionType = 'becoming_balanced' | 'becoming_unbalanced' | null;

const detectTransition = (
  currentNetAmount: number,
  newNetAmount: number,
): TransitionType => {
  if (currentNetAmount !== 0 && newNetAmount === 0) return 'becoming_balanced';
  if (currentNetAmount === 0 && newNetAmount !== 0)
    return 'becoming_unbalanced';
  return null;
};

export type AddRawTransactionToPaymentModalUserProps = {
  rawTransactions: BasicRawTransactionFragment[];
  onAttached?: (rawTransactionIds: string[]) => void;
};

export const AddRawTransactionToPaymentModal = (
  props: AddRawTransactionToPaymentModalUserProps & SystemProps,
) => {
  const {rawTransactions: rawTransactionsToAdd, onAttached, onRequestClose} =
    props;
  const rawTransactionsKey = rawTransactionsToAdd
    .map((rt) => rt._id)
    .join(',');
  const addedAmount = rawTransactionsToAdd.reduce(
    (sum, rt) => sum + rt.amount,
    0,
  );
  const addedCurrency = rawTransactionsToAdd[0]?.currency ?? 'USD';
  const [search, setSearch] = React.useState('');
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<
    string | null
  >(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [showFilters, setShowFilters] = React.useState(false);

  // Filter state
  const [netAmountFilter, setNetAmountFilter] =
    React.useState<NetAmountFilter>(NetAmountFilter.All);
  const [categories, setCategories] = React.useState<string[] | null>(null);
  const [tags, setTags] = React.useState<string[] | null>(null);
  const [dateFilter, setDateFilter] = React.useState<DateFilterState>({
    startDate: null,
    endDate: null,
  });
  const [fieldFilters, setFieldFilters] =
    React.useState<FieldFilterInputType[] | null>(null);

  // Pagination state
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [pageIndex, setPageIndex] = React.useState(0);

  // Transition fields
  const [confirmClearFields, setConfirmClearFields] = React.useState(false);
  const [transitionPayee, setTransitionPayee] = React.useState('');
  const [transitionCategory, setTransitionCategory] = React.useState('');
  const [transitionDescription, setTransitionDescription] =
    React.useState('');
  const [transitionTransferType, setTransitionTransferType] =
    React.useState<TransferType>(TransferType.AccountTransfer);

  const hasActiveFilters =
    netAmountFilter !== NetAmountFilter.All ||
    categories !== null ||
    tags !== null ||
    dateFilter.startDate !== null ||
    dateFilter.endDate !== null ||
    fieldFilters !== null;

  const activeFilterCount =
    (netAmountFilter !== NetAmountFilter.All ? 1 : 0) +
    (categories !== null ? 1 : 0) +
    ((tags !== null || fieldFilters !== null) ? 1 : 0) +
    (dateFilter.startDate !== null || dateFilter.endDate !== null ? 1 : 0);

  const filterVariables = React.useMemo<Partial<TransactionsQueryVariables>>(
    () => ({
      limit: PAGE_SIZE,
      // Conversions can't be extended, and they store the home currency at
      // the transaction level, so the currency filter alone would leave them
      // in. Both filters run before pagination so pages arrive full.
      excludeTransferTypes: [TransferType.Conversion],
      // Only same-currency transactions can accept these rows; filtering
      // server-side keeps every page full of real candidates.
      currency: addedCurrency,
      ...(search ? {search} : {}),
      ...(netAmountFilter !== NetAmountFilter.All ? {netAmountFilter} : {}),
      ...(categories ? {categories} : {}),
      ...(tags ? {tags} : {}),
      ...(dateFilter.startDate ? {startDate: dateFilter.startDate} : {}),
      ...(dateFilter.endDate ? {endDate: dateFilter.endDate} : {}),
      ...(fieldFilters ? {fieldFilters} : {}),
    }),
    [
      addedCurrency,
      search,
      netAmountFilter,
      categories,
      tags,
      dateFilter.startDate,
      dateFilter.endDate,
      fieldFilters,
    ],
  );

  const [loadTransactions, {data, loading, error}] =
    useTransactionsLazyQuery({
      notifyOnNetworkStatusChange: true,
      // no-cache, not network-only: this modal pages with Prev/Next and
      // renders each response as exactly one page. The transactions field
      // policy (ApolloProvider) appends cursor fetches to the accumulated
      // list-view entry, so reading through the cache would show every page
      // at once here — and pollute the list view's entry in the process.
      fetchPolicy: 'no-cache',
    });

  const [addConsumption, {loading: attaching}] =
    useAddConsumptionToTransactionMutation();

  const pageData = data?.transactions;
  const transactions = pageData?.items ?? [];
  const displayError = submitError ?? error?.message ?? null;
  const hasNextPage = pageData?.hasMore ?? false;
  const hasPrevPage = pageIndex > 0;

  // Transition detection
  const selectedTransaction = transactions.find(
    (t) => t._id === selectedTransactionId,
  );
  const newNetAmount =
    selectedTransaction && rawTransactionsToAdd.length > 0
      ? selectedTransaction.netAmount + addedAmount
      : null;
  const transition =
    selectedTransaction && newNetAmount !== null
      ? detectTransition(selectedTransaction.netAmount, newNetAmount)
      : null;

  // Reset transition fields when selection changes. The payee seeds from the
  // selection: a transfer becoming a payment usually keeps its counterparty
  // (the server falls back to it too), so the field starts filled rather
  // than forcing a retype.
  React.useEffect(() => {
    setConfirmClearFields(false);
    setTransitionPayee(selectedTransaction?.payee ?? '');
    setTransitionCategory('');
    setTransitionDescription(selectedTransaction?.description ?? '');
    setTransitionTransferType(
      selectedTransaction?.transferType ?? TransferType.AccountTransfer,
    );
  }, [selectedTransactionId]);

  // Load first page when filters/search change
  React.useEffect(() => {
    setCursorStack([]);
    setPageIndex(0);
    setSelectedTransactionId(null);
    void loadTransactions({variables: filterVariables});
  }, [rawTransactionsKey, filterVariables, loadTransactions]);

  const handleSearchSubmit = (query: string) => {
    const nextSearch = query.trim();
    setSubmitError(null);
    if (nextSearch === search) {
      setCursorStack([]);
      setPageIndex(0);
      void loadTransactions({variables: filterVariables});
    } else {
      setSearch(nextSearch);
    }
  };

  const handleNextPage = () => {
    if (!hasNextPage || loading || !pageData?.endCursor) return;
    const nextCursor = pageData.endCursor;
    setCursorStack((prev) => {
      const next = [...prev];
      next[pageIndex] = nextCursor;
      return next;
    });
    setPageIndex((prev) => prev + 1);
    setSelectedTransactionId(null);
    void loadTransactions({
      variables: {...filterVariables, beforeCursor: nextCursor},
    });
  };

  const handlePrevPage = () => {
    if (!hasPrevPage || loading) return;
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    setSelectedTransactionId(null);
    if (prevIndex === 0) {
      void loadTransactions({variables: filterVariables});
    } else {
      void loadTransactions({
        variables: {
          ...filterVariables,
          beforeCursor: cursorStack[prevIndex - 1],
        },
      });
    }
  };

  const handleResetFilters = () => {
    setNetAmountFilter(NetAmountFilter.All);
    setCategories(null);
    setTags(null);
    setDateFilter({startDate: null, endDate: null});
    setFieldFilters(null);
  };

  const canSubmit = (): boolean => {
    if (
      rawTransactionsToAdd.length === 0 ||
      !selectedTransaction ||
      loading ||
      attaching
    )
      return false;
    if (transition === 'becoming_balanced' && !confirmClearFields) return false;
    if (
      transition === 'becoming_unbalanced' &&
      (!transitionPayee.trim() || !transitionCategory.trim())
    )
      return false;
    return true;
  };

  const handleAttach = async (event: React.FormEvent) => {
    event.preventDefault();
    if (rawTransactionsToAdd.length === 0 || !selectedTransactionId) return;
    setSubmitError(null);
    try {
      const result = await addConsumption({
        variables: {
          transactionId: selectedTransactionId,
          rawTransactionIds: rawTransactionsToAdd.map((rt) => rt._id),
          ...(transition === 'becoming_balanced'
            ? {
                clearFields: true,
                description: transitionDescription,
                transferType: transitionTransferType,
              }
            : {}),
          ...(transition === 'becoming_unbalanced'
            ? {
                payee: transitionPayee.trim(),
                categoryName: transitionCategory.trim(),
                description: transitionDescription,
              }
            : {}),
        },
      });
      if (result.data?.addConsumptionToTransaction) {
        onAttached?.(rawTransactionsToAdd.map((rt) => rt._id));
        onRequestClose();
      }
    } catch (mutationError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      setSubmitError((mutationError as Error).message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={attaching}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="button"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleAttach}
        disabled={!canSubmit()}
      >
        {attaching ? 'Attaching...' : 'Attach'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Add to transaction"
      footer={footer}
    >
      {/* Not a <form>: the transaction search below is its own <form>, and
          nesting forms makes a mobile search submit escape to this one and
          run the attach. The primary "Attach" button drives handleAttach
          directly, so no wrapping form is needed. */}
      <div className="flex flex-col gap-4">
        <InputGroup
          label={
            rawTransactionsToAdd.length > 1
              ? `Raw transactions (${rawTransactionsToAdd.length})`
              : 'Raw transaction'
          }
        >
          {rawTransactionsToAdd.length > 0 ? (
            <div className="flex flex-col gap-2">
              {rawTransactionsToAdd.map((rt) => (
                <div
                  key={rt._id}
                  className="border-border flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex flex-col gap-1">
                    <strong>{rt.description || 'Untitled'}</strong>
                    <span className="text-muted-foreground text-sm">
                      {rt.date}
                    </span>
                  </div>
                  <AmountDisplay
                    amount={Money.fromInt(rt.amount, rt.currency)}
                    variant="plain"
                  />
                </div>
              ))}
              {rawTransactionsToAdd.length > 1 && (
                <div className="flex items-center justify-between px-3 text-sm font-semibold">
                  <span>Total</span>
                  <AmountDisplay
                    amount={Money.fromInt(addedAmount, addedCurrency)}
                    variant="plain"
                  />
                </div>
              )}
            </div>
          ) : (
            <ErrorText>Select a raw transaction to continue.</ErrorText>
          )}
        </InputGroup>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SearchInput
                inputProps={{
                  placeholder: 'Search by payee or description',
                }}
                initialQuery=""
                onSearch={handleSearchSubmit}
              />
            </div>
            <Button
              type="button"
              variant={showFilters ? 'primary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="mr-1 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 rounded-full bg-primary-foreground/30 px-1.5 text-xs">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap items-center gap-2">
              <label
                className={cn(
                  'relative flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm shadow-xs transition',
                  netAmountFilter !== NetAmountFilter.All
                    ? 'bg-primary text-primary-foreground hover:bg-primary/80'
                    : 'border-input text-foreground hover:bg-accent/30',
                )}
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                {netAmountFilterLabels[netAmountFilter]}
                <ChevronDown className="h-3.5 w-3.5" />
                <select
                  value={netAmountFilter}
                  onChange={(e) =>
                    setNetAmountFilter(parseNetAmountFilter(e.target.value))
                  }
                  className="absolute inset-0 cursor-pointer opacity-0"
                >
                  {Object.values(NetAmountFilter).map((value) => (
                    <option key={value} value={value}>
                      {netAmountFilterLabels[value]}
                    </option>
                  ))}
                </select>
              </label>
              <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
              <CategoryFilterInput
                categories={categories}
                setCategories={setCategories}
              />
              <DimensionsFilterInput
                tags={tags}
                setTags={setTags}
                fieldFilters={fieldFilters}
                setFieldFilters={setFieldFilters}
              />
              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-muted-foreground gap-1 px-2"
                  onClick={handleResetFilters}
                >
                  <X size={14} />
                  Reset
                </Button>
              )}
            </div>
          )}
        </div>
        {loading && transactions.length === 0 ? (
          <InfoText>Loading...</InfoText>
        ) : transactions.length === 0 ? (
          <InfoText>No transactions found.</InfoText>
        ) : (
          <TransactionList>
            {transactions.map((tx) => {
              const checked = selectedTransactionId === tx._id;
              const isTransfer = tx.netAmount === 0;
              return (
                <TransactionRow
                  key={tx._id}
                  onClick={() => setSelectedTransactionId(tx._id)}
                  className="cursor-pointer"
                >
                  <input
                    type="radio"
                    checked={checked}
                    onChange={() => setSelectedTransactionId(tx._id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <strong>{transactionLabel(tx)}</strong>
                      {isTransfer && (
                        <span className="text-muted-foreground rounded bg-accent px-1.5 py-0.5 text-xs">
                          Transfer
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground flex flex-wrap gap-1.5 text-sm">
                      <span>{tx.date}</span>
                      {tx.category?.name && <span>{tx.category.name}</span>}
                      {tx.description && <span>{tx.description}</span>}
                    </div>
                  </div>
                  <AmountDisplay
                    amount={Money.fromInt(tx.netAmount, tx.currency)}
                    align="right"
                  />
                </TransactionRow>
              );
            })}
          </TransactionList>
        )}
        {(hasPrevPage || hasNextPage) && (
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevPage}
              disabled={!hasPrevPage || loading}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <span className="text-muted-foreground text-sm">
              Page {pageIndex + 1}
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}
        {transition === 'becoming_balanced' && (
          <div className="border-border bg-accent/30 flex flex-col gap-3 rounded-lg border p-3">
            <p className="text-sm">
              Adding {rawTransactionsToAdd.length > 1 ? 'these raw transactions' : 'this raw transaction'}{' '}
              will make the net amount zero, converting this payment into a
              transfer. The category will be cleared; the payee is kept as the
              transfer&apos;s counterparty. Tags and fields are preserved.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmClearFields}
                onChange={(e) => setConfirmClearFields(e.target.checked)}
                className="mt-0.5"
              />
              <span>I understand, convert to transfer</span>
            </label>
            {confirmClearFields && (
              <>
                <InputGroup label="Transfer type">
                  <TransferTypeSelect
                    value={transitionTransferType}
                    onChange={setTransitionTransferType}
                  />
                </InputGroup>
                <InputGroup label="Description">
                  <TextInput
                    value={transitionDescription}
                    onChange={setTransitionDescription}
                    placeholder="Add description"
                  />
                </InputGroup>
              </>
            )}
          </div>
        )}
        {transition === 'becoming_unbalanced' && (
          <div className="border-border bg-accent/30 flex flex-col gap-3 rounded-lg border p-3">
            <p className="text-sm">
              Adding {rawTransactionsToAdd.length > 1 ? 'these raw transactions' : 'this raw transaction'}{' '}
              will make the net amount non-zero, converting this transfer into a
              payment. Please provide a payee and category.
            </p>
            <InputGroup label="Payee">
              <TextInput
                value={transitionPayee}
                onChange={setTransitionPayee}
                placeholder="Payee name"
              />
            </InputGroup>
            <InputGroup label="Category">
              <TextInput
                value={transitionCategory}
                onChange={setTransitionCategory}
                placeholder="Category name"
              />
            </InputGroup>
            <InputGroup label="Description">
              <TextInput
                value={transitionDescription}
                onChange={setTransitionDescription}
                placeholder="Add description"
              />
            </InputGroup>
          </div>
        )}
        {displayError && <ErrorText>{displayError}</ErrorText>}
      </div>
    </Modal>
  );
};
